<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <p class="text-sm text-gray-500 dark:text-gray-400">
        Configure webhooks to send onboarding data to external systems.
      </p>
      <button
        class="px-3 py-1.5 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors"
        @click="openForm(null)"
      >
        Add Webhook
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-8 text-gray-400">
      Loading webhooks...
    </div>

    <!-- Empty state -->
    <div v-else-if="webhooks.length === 0" class="text-center py-8">
      <p class="text-gray-500 dark:text-gray-400">No webhooks configured yet.</p>
    </div>

    <!-- Webhook list -->
    <div v-else class="space-y-3">
      <div
        v-for="webhook in webhooks"
        :key="webhook.id"
        class="border border-gray-200 dark:border-gray-600 rounded-lg"
      >
        <!-- Webhook row -->
        <div class="p-4">
          <div class="flex items-center justify-between">
            <div class="flex-1 min-w-0 mr-4">
              <div class="flex items-center gap-2">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {{ webhook.name }}
                </h4>
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="webhook.enabled
                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'"
                >
                  {{ webhook.enabled ? 'Active' : 'Disabled' }}
                </span>
              </div>
              <p class="text-xs text-gray-400 truncate mt-1">{{ webhook.url }}</p>
              <div class="flex items-center gap-3 mt-1 text-xs text-gray-400">
                <span>{{ webhook.success_count }} delivered</span>
                <span v-if="webhook.failure_count > 0" class="text-red-400">{{ webhook.failure_count }} failed</span>
                <span v-if="webhook.last_delivered_at">Last: {{ formatDate(webhook.last_delivered_at) }}</span>
              </div>
            </div>

            <div class="flex items-center gap-2">
              <!-- Toggle -->
              <button
                class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out"
                :class="webhook.enabled ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'"
                @click="handleToggle(webhook)"
              >
                <span
                  class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  :class="webhook.enabled ? 'translate-x-4' : 'translate-x-0'"
                />
              </button>

              <!-- Test -->
              <button
                class="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                :disabled="testingId === webhook.id"
                @click="handleTest(webhook)"
              >
                {{ testingId === webhook.id ? 'Sending...' : 'Test' }}
              </button>

              <!-- Edit -->
              <button
                class="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                @click="openForm(webhook)"
              >
                Edit
              </button>

              <!-- Delete -->
              <button
                class="px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                @click="handleDelete(webhook)"
              >
                Delete
              </button>

              <!-- Expand deliveries -->
              <button
                class="px-2 py-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                @click="toggleDeliveries(webhook)"
              >
                {{ expandedId === webhook.id ? 'Hide' : 'History' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Delivery history (expandable) -->
        <div v-if="expandedId === webhook.id" class="border-t border-gray-200 dark:border-gray-600 px-4 py-3 bg-gray-50 dark:bg-gray-900/50">
          <div v-if="loadingDeliveries" class="text-xs text-gray-400 py-2">Loading deliveries...</div>
          <div v-else-if="deliveries.length === 0" class="text-xs text-gray-400 py-2">No deliveries yet.</div>
          <div v-else class="space-y-2">
            <div
              v-for="delivery in deliveries"
              :key="delivery.id"
              class="flex items-center justify-between text-xs"
            >
              <div class="flex items-center gap-2">
                <span
                  class="inline-block w-2 h-2 rounded-full"
                  :class="{
                    'bg-green-500': delivery.status === 'success',
                    'bg-red-500': delivery.status === 'failed',
                    'bg-yellow-500': delivery.status === 'pending'
                  }"
                />
                <span class="text-gray-600 dark:text-gray-300">
                  {{ delivery.status === 'success' ? `${delivery.http_status}` : delivery.status }}
                </span>
                <span v-if="delivery.error_message" class="text-red-400 truncate max-w-[200px]">
                  {{ delivery.error_message }}
                </span>
              </div>
              <div class="flex items-center gap-3 text-gray-400">
                <span v-if="delivery.duration_ms">{{ delivery.duration_ms }}ms</span>
                <span>{{ formatDate(delivery.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Test result feedback -->
    <div v-if="testMessage" class="mt-3 p-3 rounded-lg text-sm" :class="testSuccess ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'">
      {{ testMessage }}
    </div>

    <!-- Slide panel for add/edit -->
    <UiSlidePanel ref="slidePanelRef" />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { getWebhooks, toggleWebhook, testWebhook, deleteWebhook, getDeliveries as fetchDeliveries } from '~/javascript/api/onboarding_webhooks'
import UiSlidePanel from '~/javascript/components/ui/slide_panel/slide_panel.vue'
import WebhookForm from './webhook_form.vue'

const webhooks = ref([])
const loading = ref(true)
const testingId = ref(null)
const testMessage = ref('')
const testSuccess = ref(false)
const expandedId = ref(null)
const deliveries = ref([])
const loadingDeliveries = ref(false)
const slidePanelRef = ref(null)

onMounted(async () => {
  await loadWebhooks()
})

async function loadWebhooks() {
  loading.value = true
  try {
    const response = await getWebhooks()
    webhooks.value = response.data
  } catch (e) {
    console.error('Failed to load webhooks:', e)
  } finally {
    loading.value = false
  }
}

function openForm(webhook) {
  slidePanelRef.value.open({
    component: WebhookForm,
    props: { webhook },
    events: { 'on-saved': handleSaved }
  })
}

function handleSaved() {
  slidePanelRef.value.close()
  loadWebhooks()
}

async function handleToggle(webhook) {
  try {
    const response = await toggleWebhook(webhook.id)
    const idx = webhooks.value.findIndex(w => w.id === webhook.id)
    if (idx >= 0) webhooks.value[idx] = response.data
  } catch (e) {
    console.error('Failed to toggle webhook:', e)
  }
}

async function handleTest(webhook) {
  testingId.value = webhook.id
  testMessage.value = ''
  try {
    const response = await testWebhook(webhook.id)
    testMessage.value = response.data.message || 'Test webhook sent successfully'
    testSuccess.value = true
    // Refresh to update counters
    await loadWebhooks()
  } catch (e) {
    testMessage.value = e.response?.data?.message || 'Failed to send test webhook'
    testSuccess.value = false
  } finally {
    testingId.value = null
    setTimeout(() => { testMessage.value = '' }, 5000)
  }
}

async function handleDelete(webhook) {
  if (!confirm(`Delete webhook "${webhook.name}"? This cannot be undone.`)) return
  try {
    await deleteWebhook(webhook.id)
    webhooks.value = webhooks.value.filter(w => w.id !== webhook.id)
  } catch (e) {
    console.error('Failed to delete webhook:', e)
  }
}

async function toggleDeliveries(webhook) {
  if (expandedId.value === webhook.id) {
    expandedId.value = null
    deliveries.value = []
    return
  }

  expandedId.value = webhook.id
  loadingDeliveries.value = true
  try {
    const response = await fetchDeliveries(webhook.id)
    deliveries.value = response.data
  } catch (e) {
    console.error('Failed to load deliveries:', e)
    deliveries.value = []
  } finally {
    loadingDeliveries.value = false
  }
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
</script>
